import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Copy, Check, Edit2, AlertCircle, Calendar } from 'lucide-react';
import { supabase } from '../supabase/client';
import { setProfile, setShowShareCalendar } from '../Redux/store';

/**
 * ShareCodeDisplay - Component for viewing, copying, and editing user share codes
 * Wrapped in a container with border like account info
 */
function ShareCodeDisplay() {
  const profile = useSelector((s) => s.user.profile);
  const dispatch = useDispatch();
  
  const [copied, setCopied] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newCode, setNewCode] = useState(profile?.share_code || '');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [busy, setBusy] = useState(false);

  const copyCode = () => {
    if (!profile?.share_code) return;
    navigator.clipboard.writeText(`@${profile.share_code}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleUpdate = async () => {
    if (!profile?.id || !newCode) return;
    if (newCode === profile.share_code) {
      setEditing(false);
      return;
    }

    const cleanCode = newCode.toLowerCase().trim().replace(/[^a-z0-9.]/g, '');
    if (!cleanCode) {
      setError('Invalid code format');
      return;
    }

    setBusy(true);
    setError(null);
    setSuccess(null);

    try {
      // Check uniqueness
      const { data: existing } = await supabase
        .from('profiles')
        .select('id')
        .eq('share_code', cleanCode)
        .neq('id', profile.id)
        .maybeSingle();

      if (existing) {
        setError(`@${cleanCode} already exists, please try another`);
        setBusy(false);
        return;
      }

      const { error: upError } = await supabase
        .from('profiles')
        .update({ share_code: cleanCode })
        .eq('id', profile.id);

      if (upError) throw upError;

      dispatch(setProfile({ ...profile, share_code: cleanCode }));
      setSuccess(`Updated to @${cleanCode}!`);
      setEditing(false);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Update failed');
    } finally {
      setBusy(false);
    }
  };

  if (!profile) return null;

  return (
    <div className="rounded-xl border-2 border-primary bg-gradient-to-t from-accent to-primary px-4 pb-4 pt-2 space-y-1.5">
      <label className="text-sm uppercase font-bold tracking-wider text-white ml-1">My Share Code</label>
      <div className="flex items-stretch gap-1.5 h-11">
        <div className="relative flex-1 flex items-center bg-secondary/50 rounded-xl px-3 border border-border/50">
          <span className="text-muted-foreground font-medium mr-1 select-none">@</span>
          {editing ? (
            <input
              autoFocus
              value={newCode}
              onChange={(e) => {
                setNewCode(e.target.value);
                setError(null);
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleUpdate()}
              className="bg-transparent text-sm font-medium w-full focus:outline-none"
              placeholder="your.name"
            />
          ) : (
            <span className="text-sm font-medium truncate">{profile.share_code || 'not.set'}</span>
          )}
        </div>
        
        <div className="flex gap-1.5 shrink-0">
          <button
            onClick={() => {
              if (editing) handleUpdate();
              else {
                setNewCode(profile.share_code || '');
                setEditing(true);
              }
            }}
            disabled={busy}
            className="flex items-center justify-center w-11 rounded-xl bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            {busy ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : editing ? (
              <Check className="h-5 w-5" />
            ) : (
              <Edit2 className="h-4 w-4" />
            )}
          </button>
          
          <button
            onClick={copyCode}
            className="flex items-center justify-center w-11 rounded-xl bg-blue-600 text-white shadow-sm hover:bg-blue-700 transition-colors"
          >
            {copied ? <Check className="h-5 w-5" /> : <Copy className="h-4 w-4" />}
          </button>
        </div>
      </div>
      
      {error && (
        <div className="flex items-center gap-1 mt-1.5 text-[11px] font-medium text-destructive px-1 animate-in fade-in slide-in-from-top-1 duration-200">
          <AlertCircle className="h-3 w-3" />
          {error}
        </div>
      )}
      {success && (
        <div className="flex items-center gap-1 mt-1.5 text-[11px] font-medium text-green-600 px-1 animate-in fade-in slide-in-from-top-1 duration-200">
          <Check className="h-3 w-3" />
          {success}
        </div>
      )}

      {/* View Calendar Shares button */}
      <button
        onClick={() => dispatch(setShowShareCalendar(true))}
        className="w-full rounded-xl border border-white/30 bg-white/20 hover:bg-white/30 text-white text-xs font-semibold py-2.5 transition-colors flex items-center justify-center gap-2"
        style={{ marginTop: '10px' }}
      >
        <Calendar className="h-3.5 w-3.5" />
        View Calendar Shares
      </button>
    </div>
  );
}

export default ShareCodeDisplay;
